/**
 * Head.tsx — Global site-wide <head> tags via react-helmet
 *
 * This sets the DEFAULT title, description, OG tags, and structured data.
 * Per-page overrides are set via `export const documentProps` in each +Page.tsx,
 * which the SSR renderer in +onRenderHtml.tsx picks up at build time.
 *
 * Business positioning: Business Operations Automation (BOA) for DMV businesses.
 * Not a telecom broker — a business process elimination firm.
 */
import React from "react";
import { Helmet } from "react-helmet";

const SITE_URL = "https://tenksolutions.com";
const SITE_NAME = "TenK Solutions, LLC";
const OG_IMAGE = `${SITE_URL}/img/og-image.webp`;
const PHONE = "+1-202-573-7837";
const EMAIL = "hello@tenksolutions.com";

const DEFAULT_TITLE =
  "Business Operations Automation for DMV Companies | TenK Solutions";

const DEFAULT_DESCRIPTION =
  "TenK Solutions eliminates your most time-consuming business processes through AI automation. Client intake, staff knowledge systems, and executive dashboards — built in weeks, not months. Serving DC, Maryland, and Virginia.";

const DEFAULT_KEYWORDS =
  "business operations automation, client intake automation, AI knowledge assistant, executive dashboard, workflow automation, DMV IT consulting, managed IT services DC Maryland Virginia, business process automation, AI automation for small business, telecom consulting DC";

// ─── Structured Data ─────────────────────────────────────────────────────────

const OrganizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/img/favicon.webp`,
  telephone: PHONE,
  email: EMAIL,
  sameAs: [
    "https://www.linkedin.com/company/tenksolutions",
    "https://www.instagram.com/tenksolutions",
    "https://www.facebook.com/tenksolutionsllc",
    "https://x.com/tenk_solutions",
  ],
  contactPoint: [
    {
      "@type": "ContactPoint",
      telephone: PHONE,
      contactType: "Customer Service",
      areaServed: "US",
      availableLanguage: "English",
    },
  ],
  address: {
    "@type": "PostalAddress",
    addressLocality: "Silver Spring",
    addressRegion: "MD",
    postalCode: "20902",
    addressCountry: "US",
  },
  description:
    "Business Operations Automation firm based in Montgomery County, MD, serving the DC/Maryland/Virginia metro area. We eliminate manual business processes through AI automation — from client intake to executive dashboards.",
  foundingDate: "2020",
  founder: {
    "@type": "Person",
    name: "Kiel Byrne",
  },
};

const LocalBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  name: SITE_NAME,
  image: `${SITE_URL}/img/favicon.webp`,
  "@id": SITE_URL,
  url: SITE_URL,
  telephone: PHONE,
  email: EMAIL,
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    addressLocality: "Silver Spring",
    addressRegion: "MD",
    postalCode: "20902",
    addressCountry: "US",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: 39.083997,
    longitude: -77.152757,
  },
  openingHoursSpecification: [
    {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      opens: "09:00",
      closes: "17:00",
    },
  ],
  sameAs: [
    "https://www.linkedin.com/company/tenksolutions",
    "https://www.instagram.com/tenksolutions",
    "https://www.facebook.com/tenksolutionsllc",
    "https://x.com/tenk_solutions",
  ],
  description: DEFAULT_DESCRIPTION,
};

const ServicesSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Client Intake Automation",
    serviceType: "Business Process Automation",
    description:
      "Automate scheduling, follow-ups, form collection, CRM updates, invoice generation, and document delivery for service businesses.",
    areaServed: "Washington DC, Maryland, Virginia",
    provider: { "@type": "Organization", name: SITE_NAME },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Internal Knowledge Assistant",
    serviceType: "AI Knowledge Management",
    description:
      "AI-powered internal assistant that answers staff questions about policies, processes, and procedures instantly, 24/7.",
    areaServed: "United States",
    provider: { "@type": "Organization", name: SITE_NAME },
  },
  {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Executive Dashboard",
    serviceType: "Business Intelligence",
    description:
      "Real-time business dashboard connecting revenue, pipeline, support tickets, and invoices with AI-generated weekly summaries.",
    areaServed: "United States",
    provider: { "@type": "Organization", name: SITE_NAME },
  },
];

const WebSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: SITE_NAME,
  url: `${SITE_URL}/`,
  description: DEFAULT_DESCRIPTION,
  potentialAction: {
    "@type": "SearchAction",
    target: `${SITE_URL}/?s={search_term_string}`,
    "query-input": "required name=search_term_string",
  },
};

const BreadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
    { "@type": "ListItem", position: 2, name: "Solutions", item: `${SITE_URL}/solutions` },
    { "@type": "ListItem", position: 3, name: "How It Works", item: `${SITE_URL}/how-it-works` },
    { "@type": "ListItem", position: 4, name: "Free Assessments", item: `${SITE_URL}/assessments` },
    { "@type": "ListItem", position: 5, name: "Case Studies", item: `${SITE_URL}/case-studies` },
    { "@type": "ListItem", position: 6, name: "Pricing", item: `${SITE_URL}/pricing` },
    { "@type": "ListItem", position: 7, name: "Contact Us", item: `${SITE_URL}/contact-us` },
  ],
};

// ─── Component ───────────────────────────────────────────────────────────────

const Head = () => {
  return (
    <Helmet>
      {/* Primary */}
      <title>{DEFAULT_TITLE}</title>
      <meta name="description" content={DEFAULT_DESCRIPTION} />
      <meta name="keywords" content={DEFAULT_KEYWORDS} />
      <meta name="theme-color" content="#381CC9" />
      <meta charSet="UTF-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />

      {/* Favicons */}
      <link rel="icon" href="/img/favicon.webp" />
      <link rel="apple-touch-icon" href="/img/favicon.webp" />
      <link rel="manifest" href="/manifest.json" />

      {/* Generative Engine Optimization (GEO) */}
      <link rel="alternate" type="text/markdown" href="/llms.txt" />
      <link rel="author" href={SITE_URL} />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:url" content={`${SITE_URL}/`} />
      <meta property="og:type" content="website" />
      <meta property="og:title" content={DEFAULT_TITLE} />
      <meta property="og:description" content={DEFAULT_DESCRIPTION} />
      <meta property="og:image" content={OG_IMAGE} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@tenk_solutions" />
      <meta property="twitter:domain" content="tenksolutions.com" />
      <meta property="twitter:url" content={`${SITE_URL}/`} />
      <meta name="twitter:title" content={DEFAULT_TITLE} />
      <meta name="twitter:description" content={DEFAULT_DESCRIPTION} />
      <meta name="twitter:image" content={OG_IMAGE} />

      {/* Structured Data */}
      <script type="application/ld+json">{JSON.stringify(OrganizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(LocalBusinessSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(WebSiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(BreadcrumbSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(ServicesSchema)}</script>
    </Helmet>
  );
};

export default Head;
